var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { Body } from './analytics-lib/body';
import { FrameCacheMode, FrameCacheEvent, LEFT_SIDE, ReportStatusEnum, ReportAnalyticsEnum, ReportSecondaryAnalyticsEnum, } from './types';
import { AnatomicalAnalytics } from './anatomical-analytics';
import { Vector3 } from './util/vector-utils.js';
import { FrameCache } from './frame-cache';
import { dataSetLoader2 } from './util/dataset-loader-2';
import { dataSetLoader3 } from './util/dataset-loader-3';
import { FrameType } from './types';
import { log, FACILITY as LOG_FACILITY, format } from './util/log';
import { AnalyticsTuple, AnalyticsFields, FORCE_INDEX, MOMENT_INDEX, parseTuple, } from './analytics-lib/analytics-tuple';
import { EventEmitter } from './util/event-emitter';
import { defaultAssessmentReportConfig, lowerBodyAssessmentReportConfig } from './util/assessment-report-config';
var LOG = LOG_FACILITY.ANALYTICS_ENGINE;
export var ModelStateElement;
(function (ModelStateElement) {
    ModelStateElement[ModelStateElement["QMODEL"] = 0] = "QMODEL";
    ModelStateElement[ModelStateElement["QSENSOR"] = 1] = "QSENSOR";
    ModelStateElement[ModelStateElement["PROXIMAL"] = 2] = "PROXIMAL";
    ModelStateElement[ModelStateElement["DISTAL"] = 3] = "DISTAL";
    ModelStateElement[ModelStateElement["CENTER"] = 4] = "CENTER";
    ModelStateElement[ModelStateElement["ACCEL"] = 5] = "ACCEL";
    ModelStateElement[ModelStateElement["ANG_VEL"] = 6] = "ANG_VEL";
})(ModelStateElement || (ModelStateElement = {}));
var ModelState = (function () {
    function ModelState(timestamp, state) {
        this.timestamp = timestamp;
        this.state = state;
        this.type = FrameType.ModelStateFrame;
    }
    return ModelState;
}());
export { ModelState };
export function getPartModelData(modelState, partIndex, modelStateElement) {
    return modelState.state
        ? modelState.state[partIndex][modelStateElement]
        : undefined;
}
var AnalyticsEngine = (function () {
    function AnalyticsEngine(mobileAppId) {
        var _this = this;
        this.pushQuaternions = function (frame) {
            if (!frame.readings) {
                return false;
            }
            var valid = frame.readings.reduce(function (acc, fq, index) {
                if (false === (fq[0] > 0 && fq[0] < 1.01)) {
                    log.error(LOG, "pushQuaternions() invalid Quaternion " + index + " " + format(fq));
                    acc = false;
                }
                return acc;
            }, true);
            if (!valid) {
                return false;
            }
            _this.body.update(frame);
            _this.emitModelUpdate();
            return true;
        };
        this.pushKeypoints = function (keypoints) {
            if (_this.body.updateFromKeypoints(keypoints)) {
                _this.emitModelUpdate();
            }
        };
        this.setActive = function (config) {
            if (config === undefined ||
                config.enabledFeatures === undefined ||
                !config.enabledFeatures.length) {
                _this.displayConfig.enabledFeatures = [];
                return;
            }
            _this.displayConfig.centerOfMass.delta = config.centerOfMass.delta;
            _this.displayConfig.centerOfMass.heightOffset =
                config.centerOfMass.heightOffset;
            _this.displayConfig.enabledFeatures = __spreadArray([], config.enabledFeatures, true);
        };
        this.generateReportForDataset = function (dataset) {
            var movementReportConfig = lowerBodyAssessmentReportConfig.find(function (e) { return e.movementName.toLowerCase() === dataset.movementName.toLowerCase(); });
            if (!movementReportConfig) {
                console.log("No report config found for movement " + dataset.movementName + ". Using default config");
                movementReportConfig = defaultAssessmentReportConfig;
            }
            var generatePrimaryMetrics = function (analyticConfig) {
                switch (analyticConfig.analytic) {
                    case ReportAnalyticsEnum.GRFMax: {
                        var GRFMaxL = _this.stats('ground_force_total_left', 'max').value;
                        var GRFMaxR = _this.stats('ground_force_total_right', 'max').value;
                        return {
                            primaryMetric0Value: GRFMaxL,
                            primaryMetric0ValueDisplay: Math.round(GRFMaxL * 100),
                            primaryMetric1Value: GRFMaxR,
                            primaryMetric1ValueDisplay: Math.round(GRFMaxR * 100),
                        };
                    }
                    case ReportAnalyticsEnum.KneeLoadMax: {
                        var kneeLoadMaxL = _this.stats('knee_moment_sagital_left', 'max').value;
                        var kneeLoadMaxR = _this.stats('knee_moment_sagital_right', 'max').value;
                        return {
                            primaryMetric0Value: kneeLoadMaxL,
                            primaryMetric0ValueDisplay: Math.round(kneeLoadMaxL * 100),
                            primaryMetric1Value: kneeLoadMaxR,
                            primaryMetric1ValueDisplay: Math.round(kneeLoadMaxR * 100),
                        };
                    }
                    case ReportAnalyticsEnum.KneeLoadMax_Monopedal: {
                        var kneeLoadMaxL = _this.stats('knee_moment_sagital_left', 'max_monopedal_left').value;
                        var kneeLoadMaxR = _this.stats('knee_moment_sagital_right', 'max_monopedal_right').value;
                        return {
                            primaryMetric0Value: kneeLoadMaxL,
                            primaryMetric0ValueDisplay: Math.round(kneeLoadMaxL * 100),
                            primaryMetric1Value: kneeLoadMaxR,
                            primaryMetric1ValueDisplay: Math.round(kneeLoadMaxR * 100),
                        };
                    }
                    case ReportAnalyticsEnum.KneeFlexionMax: {
                        var maxL = _this.stats('knee_angle_sagital_left', 'max').value;
                        var maxR = _this.stats('knee_angle_sagital_right', 'max').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: maxL + "\u00B0",
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: maxR + "\u00B0",
                        };
                    }
                    case ReportAnalyticsEnum.KneeFlexionMax_Monopedal: {
                        var maxL = _this.stats('knee_angle_sagital_left', 'max_monopedal_left').value;
                        var maxR = _this.stats('knee_angle_sagital_right', 'max_monopedal_right').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: maxL + "\u00B0",
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: maxR + "\u00B0",
                        };
                    }
                    case ReportAnalyticsEnum.HipKneeLoadRatioMax: {
                        var hipMaxL = _this.stats('hip_moment_sagital_left', 'max').value;
                        var hipMaxR = _this.stats('hip_moment_sagital_right', 'max').value;
                        var kneeLoadMaxL = _this.stats('knee_moment_sagital_left', 'max').value;
                        var kneeLoadMaxR = _this.stats('knee_moment_sagital_right', 'max').value;
                        var LumbarKneeLoadRatioMaxL = kneeLoadMaxL > 0 ? hipMaxL / kneeLoadMaxL : 100;
                        var LumbarKneeLoadRatioMaxR = kneeLoadMaxR > 0 ? hipMaxR / kneeLoadMaxR : 100;
                        return {
                            primaryMetric0Value: LumbarKneeLoadRatioMaxL,
                            primaryMetric0ValueDisplay: LumbarKneeLoadRatioMaxL.toFixed() || 0,
                            primaryMetric1Value: LumbarKneeLoadRatioMaxR,
                            primaryMetric1ValueDisplay: LumbarKneeLoadRatioMaxR.toFixed() || 0,
                        };
                    }
                    case ReportAnalyticsEnum.HipLoadMax: {
                        var maxL = _this.stats('hip_moment_sagital_left', 'max').value;
                        var maxR = _this.stats('hip_moment_sagital_right', 'max').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: Math.round(maxL * 100),
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: Math.round(maxR * 100),
                        };
                    }
                    case ReportAnalyticsEnum.HipLoadMax_Monopedal: {
                        var maxL = _this.stats('hip_moment_sagital_left', 'max_monopedal_left').value;
                        var maxR = _this.stats('hip_moment_sagital_right', 'max_monopedal_right').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: Math.round(maxL * 100),
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: Math.round(maxR * 100),
                        };
                    }
                    case ReportAnalyticsEnum.HipExtensionMax: {
                        var maxL = _this.stats('hip_angle_sagital_left', 'min').value * -1;
                        var maxR = _this.stats('hip_angle_sagital_right', 'min').value * -1;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: maxL + "\u00B0",
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: maxR + "\u00B0",
                        };
                    }
                    case ReportAnalyticsEnum.HipFlexionMax: {
                        var maxL = _this.stats('hip_angle_sagital_left', 'max').value;
                        var maxR = _this.stats('hip_angle_sagital_right', 'max').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: maxL + "\u00B0",
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: maxR + "\u00B0",
                        };
                    }
                    case ReportAnalyticsEnum.HipFlexionMax_Monopedal: {
                        var maxL = _this.stats('hip_angle_sagital_left', 'max').value;
                        var maxR = _this.stats('hip_angle_sagital_right', 'max').value;
                        return {
                            primaryMetric0Value: maxL,
                            primaryMetric0ValueDisplay: maxL + "\u00B0",
                            primaryMetric1Value: maxR,
                            primaryMetric1ValueDisplay: maxR + "\u00B0",
                        };
                    }
                    default:
                        console.log("generatePrimaryMetrics called on unknown analytic " + analyticConfig.analytic);
                        return {};
                }
            };
            var generateSecondaryMetric = function (analyticConfig, primaryMetrics) {
                var secondaryMetricVal = undefined;
                var secondaryMetricString = undefined;
                var statusText = undefined;
                var status = undefined;
                switch (analyticConfig.secondaryAnalytic) {
                    case ReportSecondaryAnalyticsEnum.DeltaPercent:
                        secondaryMetricVal =
                            Math.abs((primaryMetrics.primaryMetric0Value -
                                primaryMetrics.primaryMetric1Value) /
                                Math.max(primaryMetrics.primaryMetric0Value, primaryMetrics.primaryMetric1Value)) * 100;
                        secondaryMetricString = "\u2206 " + Math.round(secondaryMetricVal) + "%";
                        break;
                    case ReportSecondaryAnalyticsEnum.DeltaPercentIfPositive:
                        if (primaryMetrics.primaryMetric0Value > 0 && primaryMetrics.primaryMetric1Value > 0) {
                            secondaryMetricVal =
                                Math.abs((primaryMetrics.primaryMetric0Value -
                                    primaryMetrics.primaryMetric1Value) /
                                    Math.max(primaryMetrics.primaryMetric0Value, primaryMetrics.primaryMetric1Value)) * 100;
                            secondaryMetricString = "\u2206 " + Math.round(secondaryMetricVal) + "%";
                        }
                        break;
                    case ReportSecondaryAnalyticsEnum.DeltaAbsolute:
                        secondaryMetricVal =
                            primaryMetrics.primaryMetric0Value -
                                primaryMetrics.primaryMetric1Value;
                        secondaryMetricString =
                            "\u2206 " + (secondaryMetricVal < 10 ? secondaryMetricVal.toFixed(2) : Math.round(secondaryMetricVal));
                        break;
                    default:
                        break;
                }
                if (secondaryMetricVal !== undefined) {
                    statusText = generateStatusText(analyticConfig, primaryMetrics, secondaryMetricVal);
                    status = !analyticConfig.statusThresholds
                        ? undefined
                        : secondaryMetricVal < analyticConfig.statusThresholds[0]
                            ? ReportStatusEnum.Normal
                            : secondaryMetricVal < analyticConfig.statusThresholds[1]
                                ? ReportStatusEnum.Warning
                                : ReportStatusEnum.Danger;
                }
                return {
                    secondaryMetric: secondaryMetricString,
                    statusText: statusText,
                    status: status,
                };
            };
            var generateStatusText = function (analyticConfig, primaryMetrics, secondaryMetricVal) {
                if (!analyticConfig.statusThresholds || !analyticConfig.statusText) {
                    return;
                }
                var statusText = secondaryMetricVal < analyticConfig.statusThresholds[0]
                    ? analyticConfig.statusText[0]
                    : secondaryMetricVal < analyticConfig.statusThresholds[1]
                        ? analyticConfig.statusText[1]
                        : analyticConfig.statusText[2];
                if (typeof statusText === 'function') {
                    var leftRightBias = primaryMetrics.primaryMetric0Value > primaryMetrics.primaryMetric1Value ? 'L' : 'R';
                    return statusText({ leftRight: leftRightBias });
                }
                return statusText;
            };
            var report = {
                timestamp: dataset.startTime,
                movementName: dataset.movementName,
                datasetId: dataset.id,
                analytics: movementReportConfig.analytics.map(function (analyticConfig) {
                    var primaryMetrics = generatePrimaryMetrics(analyticConfig);
                    var secondaryMetric = generateSecondaryMetric(analyticConfig, primaryMetrics);
                    var analyticReport = __assign({ title: analyticConfig.title, primaryMetric0Label: analyticConfig.primaryMetric0Label, primaryMetric0Value: primaryMetrics.primaryMetric0ValueDisplay, primaryMetric1Label: analyticConfig.primaryMetric1Label, primaryMetric1Value: primaryMetrics.primaryMetric1ValueDisplay }, secondaryMetric);
                    return analyticReport;
                }),
            };
            return report;
        };
        log.info(LOG, 'AnalyticsEngine constructor');
        this.displayConfig = new AnatomicalAnalytics();
        this.frameCache = new FrameCache(this);
        this.lastCoM = null;
        this.movementName = 'FREE PLAY';
        this.mobileAppId = mobileAppId;
        this.obcTopId = 'NO_OBC_TOP_ID';
        this.obcBottomId = 'NO_OBC_BOTTOM_ID';
        this.harnessTopId = 'NO_TOP_ID';
        this.harnessBottomId = 'NO_BOT_ID';
        this.body = new Body();
    }
    AnalyticsEngine.prototype.getPlayback = function () {
        return this.frameCache.playback;
    };
    AnalyticsEngine.prototype.setLiveMode = function () {
        this.frameCache.setMode(FrameCacheMode.Live);
    };
    AnalyticsEngine.prototype.setLiveCameraMode = function () {
        this.frameCache.setMode(FrameCacheMode.LiveCamera);
    };
    AnalyticsEngine.prototype.setMobileAppId = function (mobileAppId) {
        this.mobileAppId = mobileAppId;
    };
    AnalyticsEngine.prototype.setMovementName = function (movementName) {
        this.movementName = movementName;
    };
    AnalyticsEngine.prototype.setSerials = function (obcTopId, obcBottomId, harnessTopId, harnessBottomId) {
        this.obcTopId = obcTopId;
        this.obcBottomId = obcBottomId;
        this.harnessTopId = harnessTopId;
        this.harnessBottomId = harnessBottomId;
    };
    AnalyticsEngine.prototype.initialize = function () {
        log.info(LOG, 'initialize');
        this.displayConfig = new AnatomicalAnalytics();
        this.frameCache.initialize();
        this.body = new Body();
        this.lastCoM = new Vector3(0, 0, 0);
    };
    AnalyticsEngine.prototype.setActiveAnalytics = function (config) {
        this.setActive(config);
    };
    AnalyticsEngine.prototype.getAlignment = function () {
        return this.body.alignment;
    };
    AnalyticsEngine.prototype.getAnalyticsTuple = function (timestamp) {
        var grf = this.body.getGroundReactionForce();
        var grfAccel = this.body.getGroundReactionForceAccel();
        var sacral = this.body.getSacral();
        var grfDistribution = grf[FORCE_INDEX][LEFT_SIDE].norm();
        var hips = this.body.getHip(grfDistribution, sacral[MOMENT_INDEX]);
        var com = [this.body.fullBody.getCm().round(3)];
        var result = new AnalyticsTuple(timestamp, [
            grf,
            grfAccel,
            sacral,
            hips,
            this.body.getKnee(hips),
            com,
        ]);
        return result;
    };
    AnalyticsEngine.prototype.setAlignment = function (rotations, transforms) {
        log.info(LOG, 'setAlignment');
        this.body.setAlignment(rotations, transforms);
    };
    AnalyticsEngine.prototype.getModelStateFrame = function (timestamp) {
        var _this = this;
        var state = this.body.partMap.map(function (name) {
            return _this.body.kchain[name].getModelStateElements();
        });
        return new ModelState(timestamp, state);
    };
    AnalyticsEngine.prototype.emitDataSetLoaded = function (dataSetVersion) {
        EventEmitter.emit(FrameCacheEvent.DataSetLoaded, dataSetVersion);
    };
    AnalyticsEngine.prototype.emitModelUpdate = function () {
        var timestamp = this.body.kchain.hip.timestamp;
        EventEmitter.emitModelUpdate({
            state: this.getModelStateFrame(timestamp),
            analyticsTuple: this.getAnalyticsTuple(timestamp),
        });
    };
    AnalyticsEngine.prototype.captureBegin = function () {
        this.frameCache.captureBegin(this.movementName);
    };
    AnalyticsEngine.prototype.captureComplete = function () {
        this.frameCache.captureComplete();
    };
    AnalyticsEngine.prototype.loadDataSet = function (json) {
        log.debug(LOG, "loadDataSet begin " + json.version + " " + json.movementName);
        var loaded = false;
        this.initialize();
        if (json.version === '2.0') {
            loaded = dataSetLoader2(json, this);
        }
        else if (json.version === '3.0' ||
            json.version === '3.1' ||
            json.version === '3.2' ||
            json.version === '3.3' ||
            json.version === '3.4') {
            loaded = dataSetLoader3(json, this);
        }
        if (loaded) {
            this.frameCache.setMode(FrameCacheMode.Playback);
            this.frameCache.playback.load(Array.from(this.frameCache.timingsSet));
            this.frameCache.playback.scrubToIndex(0);
            this.emitDataSetLoaded(json.version);
        }
        log.info(LOG, "loadDataSet complete success:" + loaded);
        return false;
    };
    AnalyticsEngine.prototype.stats = function (key, stat) {
        switch (stat) {
            case 'max': {
                var max = this.timeseries.all.reduce(function (acc, ts) {
                    if (!acc.value || ts[key] > acc.value) {
                        acc.value = ts[key];
                        acc.index = ts.index;
                        acc.timestamp = ts.timestamp;
                        return acc;
                    }
                    return acc;
                }, {});
                return max;
            }
            case 'min': {
                var min = this.timeseries.all.reduce(function (acc, ts) {
                    if (!acc.value || ts[key] < acc.value) {
                        acc.value = ts[key];
                        acc.index = ts.index;
                        acc.timestamp = ts.timestamp;
                        return acc;
                    }
                    return acc;
                }, {});
                return min;
            }
            case 'max_monopedal_right': {
                var max = this.timeseries.all.reduce(function (acc, ts) {
                    if (ts['ground_force_total_left'] === 0) {
                        if (!acc.value || ts[key] > acc.value) {
                            acc.value = ts[key];
                            acc.index = ts.index;
                            acc.timestamp = ts.timestamp;
                            return acc;
                        }
                    }
                    return acc;
                }, {});
                return max;
            }
            case 'max_monopedal_left': {
                var max = this.timeseries.all.reduce(function (acc, ts) {
                    if (ts['ground_force_total_right'] === 0) {
                        if (!acc.value || ts[key] > acc.value) {
                            acc.value = ts[key];
                            acc.index = ts.index;
                            acc.timestamp = ts.timestamp;
                            return acc;
                        }
                    }
                    return acc;
                }, {});
                return max;
            }
            default:
                return {};
        }
    };
    Object.defineProperty(AnalyticsEngine.prototype, "timeseries", {
        get: function () {
            var analyticTuples = this.frameCache.analyticTuples;
            var timings = this.frameCache.timingsSet;
            return {
                get fields() {
                    return AnalyticsFields;
                },
                get all() {
                    var result = [];
                    Array.from(timings, function (timestamp) {
                        var envelope = analyticTuples.get(timestamp);
                        if (envelope) {
                            var obj_1 = {
                                index: envelope.index,
                                timestamp: timestamp,
                            };
                            AnalyticsFields.forEach(function (fieldSpec) {
                                obj_1[fieldSpec.key] = parseTuple(fieldSpec.key, envelope.tuple);
                            });
                            result.push(obj_1);
                        }
                        return 0;
                    });
                    return result;
                },
            };
        },
        enumerable: false,
        configurable: true
    });
    return AnalyticsEngine;
}());
export { AnalyticsEngine };
var _analyticsEngine = null;
export function getAnalyticsEngine(mobileAppId) {
    if (mobileAppId === void 0) { mobileAppId = 'NO_MOBILE_APP_ID'; }
    if (!_analyticsEngine) {
        _analyticsEngine = new AnalyticsEngine(mobileAppId);
    }
    return _analyticsEngine;
}
//# sourceMappingURL=analytics-engine.js.map